import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button, Card, Typography, Alert, Space, Steps, Form, Select, Row, Col, Divider, message, Popover, Modal } from 'antd';
import { WarningOutlined, QuestionCircleOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { VehicleFormData, VehicleHistoryReport, ImageObject } from '../../types/vehicle';
import { ImageReview } from './image_review';
import { VehicleDetailsReviewPage } from './vehicle_details_review_page';
import HistoryReportReview from './history_report_review';
import CreatePpiReport from '../ppi-report/create_ppi_report';
import { useVehicleData } from '../../hooks/useVehicleData';
import { authPpiApiAxiosWithRefresh, getPrices } from '../../services/api';
import { useUser } from '../../context/UserContext';

import { PpiAnalysisProvider } from '../ppi-report/contexts/PpiAnalysisContext';
import TopBar from '../TopBar/TopBar';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Helper function to copy text to clipboard
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    message.success('VIN copied to clipboard!');
  } catch (err) {
    message.error('Failed to copy VIN');
  }
};

// Define vital fields for validation
const VITAL_FIELDS = ['VIN', 'titleStatus', 'Mileage', 'Engine', 'Drivetrain', 'Transmission'];

interface MissingDetailsPageProps {
  missingFields: string[];
  formData: VehicleFormData;
  docId: string;
  onUpdate: (data: Partial<VehicleFormData>) => void;
  onContinue: () => void;
  onBack: () => void;
}

const MissingDetailsPage: React.FC<MissingDetailsPageProps> = ({
  missingFields,
  formData,
  docId,
  onUpdate,
  onContinue,
  onBack
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { updateVehicleData } = useVehicleData(docId);

  const fieldLabels = {
    VIN: 'VIN',
    titleStatus: 'Title Status',
    Mileage: 'Mileage',
    Engine: 'Engine',
    Drivetrain: 'Drivetrain',
    Transmission: 'Transmission'
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      // Save to backend first
      await updateVehicleData(values);
      message.success('Missing details saved successfully');
      // Then update local state
      onUpdate(values);
    } catch (err) {
      message.error('Failed to save missing details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <WarningOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
        <Title level={3}>Important Vehicle Details Missing!</Title>
        <Paragraph>
          These details are really important for generating an accurate PPI report.
          Try to get this information from the seller.
        </Paragraph>

      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={formData}
      >
        <Row gutter={16}>
          {missingFields.map((field) => (
            <Col span={12} key={field}>
              {field === 'titleStatus' ? (
                <Form.Item
                  name={field}
                  label={
                    <Space>
                      {fieldLabels[field as keyof typeof fieldLabels]}
                      <Popover
                        content={
                          <div style={{ maxWidth: 300 }}>
                            <div>
                              <Text strong>Need help determining title status?</Text>
                              <br />
                              <Text>
                                Check if this vehicle has been reported stolen or salvaged using the NICB database. Copy the VIN below and paste it into the search bar on the NICB website.
                              </Text>
                              <br />
                              <br />
                              <Text >
                                💡 <strong>Tip:</strong> While NICB provides theft/salvage records, a vehicle history report (Carfax) offers more comprehensive title information.
                              </Text>
                            </div>
                            <div style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                              <Text strong>{formData.VIN || 'VIN not available! You should ask the seller for the VIN.'}</Text>
                            </div>
                            <Space>
                              <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => copyToClipboard(formData.VIN || '')}
                                disabled={!formData.VIN}
                              >
                                Copy VIN
                              </Button>
                              <Button
                                size="small"
                                type="primary"
                                icon={<LinkOutlined />}
                                onClick={() => window.open('https://www.nicb.org/vincheck', '_blank')}
                              >
                                Check VIN
                              </Button>
                            </Space>
                          </div>
                        }
                        title="Title Status Help"
                        trigger="hover"
                      >
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', fontSize: '16px' }} />
                      </Popover>
                    </Space>
                  }
                >
                  <Select placeholder={`Enter ${fieldLabels[field as keyof typeof fieldLabels]}`}>
                    <Option value="Clean">Clean</Option>
                    <Option value="Salvage">Salvage</Option>
                    <Option value="Rebuilt">Rebuilt</Option>
                    <Option value="Parts Only">Parts Only</Option>
                  </Select>
                </Form.Item>
              ) : (
                <Form.Item
                  name={field}
                  label={fieldLabels[field as keyof typeof fieldLabels]}
                >
                  <Input placeholder={`Enter ${fieldLabels[field as keyof typeof fieldLabels]}`} />
                </Form.Item>
              )}
            </Col>
          ))}
        </Row>

        <Divider />

        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <Button onClick={onBack} >
            Back
          </Button>
          <Space wrap>
            <Button onClick={onContinue}>
              Continue Without Details
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Update & Continue
            </Button>
          </Space>
        </Space>
      </Form>
    </Card>
  );
};


const steps = [
  { title: 'Get Started', description: 'Enter a URL or add details yourself' },
  { title: 'Vehicle Details', description: 'Add and confirm vehicle information' },
  { title: 'Missing Info', description: 'Complete Vital Information' },
  { title: 'Images', description: 'Add Photos (optional)' },
  { title: 'History Reports', description: 'Upload a History Report' },
  { title: 'Generate Report', description: 'Get AI-Powered Inspection Report' }
];

const WebScraperCustomPPIRevamped: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Analyzing vehicle details. This may take a few minutes...');
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<VehicleFormData | null>(null);
  const [missingVitalFields, setMissingVitalFields] = useState<string[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState<'scrape' | 'manual' | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { credits } = useUser();
  const [prices, setPrices] = useState({ quick_run_cost: 1, full_run_cost: 6 });

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const pricesData = await getPrices();
        if (pricesData.quick_run_cost && pricesData.full_run_cost) {
          setPrices(pricesData);
        }
      } catch (error) {
        console.error("Failed to fetch prices", error);
      }
    };
    fetchPrices();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hasEnoughCredits = credits >= prices.full_run_cost;
  // Get docId from URL params
  const docId = searchParams.get('docId');


  // Function to load formData from Firestore
  const loadFormData = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const response = await (await authPpiApiAxiosWithRefresh()).get(`/vehicle-data/${docId}`);

      if (response.data.success) {
        const fetchedData = response.data.data;
        const vehicleDetails = fetchedData.vehicleDetails || {};

        // --- START: New Image Handling Logic ---
        let scrapedImages: ImageObject[] = fetchedData.scraped_image_links_gcs || [];
        const originalScrapedUrls: string[] = (fetchedData.vehicleDetails && fetchedData.vehicleDetails.imageLinks) || [];

        // If GCS links are not populated yet, but original links exist, use them as a fallback
        if (scrapedImages.length === 0 && originalScrapedUrls.length > 0) {
          scrapedImages = originalScrapedUrls.map(url => ({
            url: url,
            status: 'pending_classification', // Status is technically unknown from this fallback
            category: null
          }));
        }

        const uploadedImages: ImageObject[] = fetchedData.user_uploaded_images || [];
        const allImageLinks: ImageObject[] = [...scrapedImages, ...uploadedImages];
        // --- END: New Image Handling Logic ---

        const historyReports = fetchedData.historyReports || [];

        setFormData({
          ...vehicleDetails,
          description: fetchedData.vehicleDescription || '',
          comments: fetchedData.userComments || '',
          imageLinks: allImageLinks,
          historyReports: historyReports,
        });

        // Re-check missing vital fields after loading data
        const missing = checkMissingVitalFields({
          ...vehicleDetails,
          description: fetchedData.vehicleDescription || '',
          comments: fetchedData.userComments || '',
          imageLinks: allImageLinks,
          historyReports: historyReports,
        });
        setMissingVitalFields(missing);

      } else {
        setError(response.data.error || 'Failed to load vehicle data.');
      }
    } catch (err) {
      setError('Error loading vehicle data.');
      console.error('Error loading vehicle data:', err);
    } finally {
      setLoading(false);
    }
  }, [docId]);

  // Effect to load formData if docId is present
  useEffect(() => {
    if (docId) {
      loadFormData();
    }

    const step = searchParams.get('step');
    if (step && !isNaN(Number(step))) {
      setCurrentStep(Number(step));
    } else if (docId) {
      // If docId exists but no step, start at vehicle details
      setCurrentStep(1);
    }
  }, [searchParams, docId, loadFormData]); // Added loadFormData to dependency array

  useEffect(() => {
    if (loading) {
      setLoadingMessage('Analyzing vehicle details. This may take a few minutes...');
      const timer1 = setTimeout(() => {
        setLoadingMessage('gathering vehicle details and description...');
      }, 15000);
      const timer2 = setTimeout(() => {
        setLoadingMessage('gathering images...');
      }, 30000);
      const timer3 = setTimeout(() => {
        setLoadingMessage('almost there, hold on while we validate results...');
      }, 45000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [loading]);

  const handleContinue = () => {
    if (docId) {
      updateStep(1); // Go to the next step
    }
  };

  const checkMissingVitalFields = (data: VehicleFormData): string[] => {
    return VITAL_FIELDS.filter(field => !data[field] || data[field].toString().trim() === '');
  };

  // Helper function to update both step state and URL
  const updateStep = (step: number) => {
    setCurrentStep(step);
    if (docId) {
      setSearchParams({ docId, step: step.toString() });
    }
  };

  const handleScrape = async (confirmed = false) => {
    if (docId && !confirmed) {
      setModalAction('scrape');
      setModalVisible(true);
      return;
    }

    if (!url) {
      setError('Please enter a URL');
      return;
    }

    setLoading(true);
    setError(null);
    setModalVisible(false);

    try {
      const response = await (
        await authPpiApiAxiosWithRefresh()
      ).post('/scrape-webpage-custom-ppi', {
        url: url
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });


      if (response.data.success) {
        setFormData(response.data.formData);
        const newDocId = response.data.firestore.doc_id;
        setSearchParams({ docId: newDocId, step: '1' });
        setCurrentStep(1);
      } else {
        setError(response.data.error || 'Failed to scrape webpage');
      }
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred while scraping the webpage');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = async (confirmed = false) => {
    if (docId && !confirmed) {
      setModalAction('manual');
      setModalVisible(true);
      return;
    }

    setLoading(true);
    setError(null);
    setModalVisible(false);

    try {
      const response = await (
        await authPpiApiAxiosWithRefresh()
      ).post('/create-manual-ppi', {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        const newDocId = response.data.doc_id;
        setFormData({} as VehicleFormData);
        setSearchParams({ docId: newDocId, step: '1' });
        setCurrentStep(1);
      } else {
        setError(response.data.error || 'Failed to create manual report.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while creating the report.');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleDetailsSubmit = (data: VehicleFormData) => {
    setFormData(data);

    // Check for missing vital fields
    const missing = checkMissingVitalFields(data);
    setMissingVitalFields(missing);

    if (missing.length > 0) {
      updateStep(2); // Go to missing details page
    } else {
      updateStep(3); // Skip to images page
    }
  };

  const handleMissingDetailsUpdate = (data: Partial<VehicleFormData>) => {
    if (formData) {
      const updatedData = { ...formData, ...data };
      setFormData(updatedData);
      updateStep(3); // Go to images page
    }
  };

  const handleMissingDetailsContinue = () => {
    updateStep(3); // Continue without updating missing details
  };

  const handleImageSubmit = (images: ImageObject[]) => {
    if (formData) {
      setFormData({ ...formData, imageLinks: images });
      updateStep(4);
    }
  };

  const handleHistoryReportSubmit = (reports: VehicleHistoryReport[]) => {
    if (formData) {
      setFormData({ ...formData, historyReports: reports });
      updateStep(5);
    }
  };

  const handleReportComplete = () => {
    updateStep(0);
    setFormData(null);
    setUrl('');
    setMissingVitalFields([]);
    // Clear URL params
    setSearchParams({});
  };

  const goBack = () => {
    if (currentStep > 0) {
      // Skip missing details step if no missing fields when going back
      if (currentStep === 3 && missingVitalFields.length === 0) {
        updateStep(1);
      } else if (currentStep === 2) {
        updateStep(1);
      } else {
        updateStep(currentStep - 1);
      }
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Card>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Title level={3}>Enter Vehicle Listing URL</Title>
                <Text type="secondary">
                  Just drop the URL —— we'll pull the details and build your report instantly.
                </Text>
              </div>

              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Input
                  size="large"
                  placeholder="Paste a listing URL (e.g., Bring a Trailer, Cars and Bids, Autotrader)"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onPressEnter={() => handleScrape()}
                  disabled={loading}
                />

                <div style={{ textAlign: 'center' }}>
                  <Space direction="vertical">
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => handleScrape()}
                      loading={loading}
                      disabled={!url || loading || !hasEnoughCredits}
                      style={{ whiteSpace: 'normal', height: 'auto' }}
                    >
                      {loading ? loadingMessage : (docId ? 'Generate another Report' : 'Generate a Report')}
                    </Button>
                    <Text type="secondary">
                      Cost: {prices.full_run_cost} credit{prices.full_run_cost !== 1 ? 's' : ''}.
                      {!hasEnoughCredits && " You don't have enough credits."}
                    </Text>
                  </Space>
                </div>

                <Divider style={{ borderColor: 'black', borderWidth: '2px' }}>OR</Divider>

                <div style={{ textAlign: 'center' }}>
                  <Title level={4}>No link? Enter details yourself.</Title>
                  <Text type="secondary">
                    Use this for trade offers, in-person inspections, or if your URL isn't automatically supported.
                  </Text>
                  <div style={{ marginTop: 16 }}>
                    <Space direction="vertical">
                      <Button
                        type="primary"
                        ghost
                        size="large"
                        onClick={() => handleManualEntry()}
                        loading={loading}
                        disabled={loading || !hasEnoughCredits}
                      >
                        {loading ? 'Creating Report...' : (docId ? 'Build another Report' : 'Build a Report')}
                      </Button>
                      <Text type="secondary">
                        Cost: {prices.full_run_cost} credit{prices.full_run_cost !== 1 ? 's' : ''}.
                        {!hasEnoughCredits && " You don't have enough credits."}
                      </Text>
                    </Space>
                  </div>
                </div>
              </Space>

              <Modal
                title="Warning"
                visible={modalVisible}
                onOk={() => modalAction === 'scrape' ? handleScrape(true) : handleManualEntry(true)}
                onCancel={() => setModalVisible(false)}
                okText="Proceed"
                cancelText="Cancel"
                confirmLoading={loading}
              >
                <p>Proceeding will erase the existing report. Are you sure you want to continue?</p>
              </Modal>
            </Card>

            {loading && (
              <Alert
                message="⚡ Please keep this page open while we finish gathering your data. We're gathering listing details, which may take up to 1-2 minutes."
                type="warning"
                style={{ marginTop: 24 }}
              />
            )}

            {docId && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleContinue}
                  disabled={loading}
                >
                  Continue with Existing Report
                </Button>
              </div>
            )}

            {error && (
              <Alert
                message=""
                description={error}
                type="error"
                showIcon
                style={{ marginTop: 24 }}
              />
            )}
          </div>
        );

      case 1:
        return docId ? (
          <VehicleDetailsReviewPage
            docId={docId}
            onNext={(vehicleData) => handleVehicleDetailsSubmit(vehicleData)}
            onBack={goBack}
          />
        ) : null;

      case 2:
        return formData && docId ? (
          <MissingDetailsPage
            missingFields={missingVitalFields}
            formData={formData}
            docId={docId}
            onUpdate={handleMissingDetailsUpdate}
            onContinue={handleMissingDetailsContinue}
            onBack={goBack}
          />
        ) : null;

      case 3:
        return formData ? (
          <ImageReview
            initialImages={formData.imageLinks || []}
            onSubmit={handleImageSubmit}
            onBack={goBack}
            docId={docId || undefined}
            onUploadSuccess={loadFormData}
            isManual={false}
          />
        ) : null;

      case 4:
        return formData && docId ? (
          <HistoryReportReview
            docId={docId}
            vin={formData.VIN || ''} // Pass the VIN to the component
            initialReports={formData.historyReports || []}
            onSubmit={handleHistoryReportSubmit}
            onBack={goBack}
          />
        ) : null;

      case 5:
        return docId ? (
          <CreatePpiReport
            docId={docId}
            onComplete={handleReportComplete}
            onBack={goBack}
            vehicleDetails={formData || undefined}
          />
        ) : null;

      default:
        return null;
    }
  };

  // Filter steps to show - hide missing details step if no missing fields
  const visibleSteps = steps.filter((step, index) => {
    if (index === 2) { // Missing details step
      return missingVitalFields.length > 0;
    }
    return true;
  });

  const getVisibleStepIndex = (actualStep: number): number => {
    if (actualStep <= 1) return actualStep;
    if (actualStep === 2 && missingVitalFields.length === 0) return 2; // Images step
    if (actualStep === 2 && missingVitalFields.length > 0) return 2; // Missing details step
    if (actualStep >= 3 && missingVitalFields.length === 0) return actualStep - 1;
    return actualStep;
  };

  return (
    <div>

      <TopBar className="title" title="" />
      <PpiAnalysisProvider docId={docId}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
          {(!isMobile || currentStep === 0) && (
            <Title level={2}>Create your AI-powered Condition Report</Title>
          )}

          {!isMobile && (
            <Steps
              current={getVisibleStepIndex(currentStep)}
              items={visibleSteps}
              style={{ marginBottom: 32 }}
            />
          )}

          {renderCurrentStep()}
        </div>
      </PpiAnalysisProvider>
    </div>
  );
};

export default WebScraperCustomPPIRevamped;